import React from "react";
import logger from "../../utils/logger";
import { useOnboarding } from "../../contexts/OnboardingContext";
import ScreenTimeStep from "./steps/ScreenTimeStep";
import NameAgeStep from "./steps/NameAgeStep";
import ProfilePictureStep from "./steps/ProfilePictureStep";
import InterestsStep from "./steps/InterestsStep";
import DiscoverySourceStep from "./steps/DiscoverySourceStep";
import MeetStep from "./steps/MeetStep";
import AdventureStep from "./steps/AdventureStep";
import OnboardingProgress from "./OnboardingProgress";
import TrialWall from "../TrialWall";
import { useAuth } from "../../contexts/AuthContext";

const OnboardingModal = () => {
  const { showOnboarding, currentStep, prevStep } = useOnboarding();
  const { hasEntitlement, isTrialing, isGrandfathered } = useAuth();

  logger.log("🔵 OnboardingModal: Render check", {
    showOnboarding,
    currentStep,
  });

  if (!showOnboarding) return null;

  // Step 8: the mandatory paywall, rendered as PART of the flow — the story
  // loader resolves into it with no modal pop, page load, or redirect.
  // Checkout navigates to Stripe; abandoning + reloading is caught by
  // Navigation's persisted CTA flag.
  if (currentStep === 8) {
    // Entitled while on the wall (gift redeemed, comp, grandfathered): the
    // mandatory paywall has nothing to sell — close out to the app instead.
    if (hasEntitlement || isTrialing || isGrandfathered) {
      try { localStorage.removeItem("pixley_show_subscription_cta"); } catch {}
      window.location.href = "/storyverse";
      return null;
    }
    return (
      <div
        className="fixed inset-0 z-50"
        style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)" }}
      >
        <TrialWall isOpen onClose={() => {}} context={{ trigger: "post_onboarding", mandatory: true }} />
      </div>
    );
  }

  // iOS-mirrored order (NewOnboardingFlow, minus the role pre-step):
  // screenTime → name+age → photo → interests → discovery ("how did you
  // find us", kept for attribution) → meet → first adventure.
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <ScreenTimeStep />;
      case 2:
        return <NameAgeStep />;
      case 3:
        return <ProfilePictureStep />;
      case 4:
        return <InterestsStep />;
      case 5:
        return <DiscoverySourceStep />;
      case 6:
        return <MeetStep />;
      case 7:
        return <AdventureStep />;
      default:
        return <ScreenTimeStep />;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden relative"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -220, left: -200, width: 540, height: 540, borderRadius: "50%", background: "rgba(0,101,248,0.10)", filter: "blur(40px)" }} />
        <div style={{ position: "absolute", bottom: -240, right: -220, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,202,255,0.10)", filter: "blur(40px)" }} />
        <div style={{ position: "absolute", top: "35%", right: "8%", width: 220, height: 220, borderRadius: "50%", background: "rgba(255,107,154,0.06)", filter: "blur(40px)" }} />
      </div>

      {/* Top header bar */}
      <header
        className="fixed top-0 left-0 w-full z-50 flex justify-between items-center px-6 py-4"
        style={{
          background: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(14px)",
          borderBottom: "2px solid rgba(11,26,51,0.06)",
        }}
      >
        <div className="flex items-center gap-2.5">
          <img
            src="/assets/pixley-panda-nobg.png"
            alt=""
            style={{ width: 32, height: 32 }}
            onError={(e) => { e.target.src = "/assets/pixley-panda.png"; }}
          />
          <span
            style={{
              fontFamily: "'Fredoka', sans-serif",
              color: "#0065F8",
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            Pixley
          </span>
        </div>
        <OnboardingProgress />
      </header>

      {/* Scrollable content area */}
      <main className="relative z-10 h-screen overflow-y-auto px-4 sm:px-6 pt-20 pb-32">
        <div className="w-full max-w-2xl mx-auto min-h-[calc(100vh-12rem)] flex flex-col items-center justify-center">
          {renderCurrentStep()}
        </div>
      </main>

      {/* Fixed bottom nav */}
      <footer
        className="fixed bottom-0 left-0 w-full z-50 flex justify-between items-center px-6 sm:px-10 py-4 sm:py-5"
        style={{
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(18px)",
          borderTop: "2px solid rgba(11,26,51,0.06)",
          boxShadow: "0 -5px 0 rgba(11,26,51,0.04), 0 -10px 28px rgba(11,26,51,0.06)",
        }}
      >
        {currentStep > 1 ? (
          <button
            onClick={prevStep}
            className="inline-flex items-center gap-2 transition active:scale-[0.97]"
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: "#FFFFFF",
              color: "#0065F8",
              fontWeight: 700,
              fontSize: 14,
              border: "2px solid rgba(0,101,248,0.22)",
              borderRadius: 999,
              padding: "10px 18px",
              boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
              cursor: "pointer",
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back</span>
          </button>
        ) : (
          <div />
        )}
        {/* Continue button rendered here by each step via portal */}
        <div id="onboarding-continue-slot" />
      </footer>
    </div>
  );
};

export default OnboardingModal;
